import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  ClipboardList,
  Compass,
  HelpCircle,
  LayoutGrid,
  Loader2,
  Map,
  User,
  X,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { useMobileSidebar } from "@/contexts/MobileSidebarContext";
import { cn } from "@/lib/utils";
import ProgrammeSwitcher from "./ProgrammeSwitcher";

// 2πr for the r=27 progress ring
const RING_CIRCUMFERENCE = 169.6;

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
}

function NavSection({
  title,
  items,
  onNavigate,
}: {
  title: string;
  items: NavItem[];
  onNavigate: () => void;
}) {
  const location = useLocation();

  return (
    <>
      <h2 className="px-2 pt-6 pb-2.5 text-[10.5px] font-bold uppercase tracking-[.18em] text-[#F2EFE3]/[.42]">
        {title}
      </h2>
      <nav className="flex flex-col gap-1">
        {items.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              className={cn(
                "flex items-center gap-3 rounded-[9px] px-3 py-[11px] text-[14.5px] font-semibold transition-colors",
                isActive
                  ? "bg-white/10 text-white"
                  : "text-[#F2EFE3]/[.72] hover:bg-white/[.06] hover:text-white"
              )}
            >
              <Icon className="w-[18px] h-[18px] flex-none" strokeWidth={1.7} />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </>
  );
}

export function Sidebar() {
  const { isOpen, close } = useMobileSidebar();
  const { enrollment, isLoading, refetch } = useEnrollment();
  const { pathname } = useLocation();

  // Overall Feedback goes stale when an admin publishes a review mid-session
  // (KC-338); refresh it in the background as the user moves around the app.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    refetch();
  }, [pathname, refetch]);

  const currentWeek = enrollment?.current_week ?? null;
  const totalWeeks = enrollment?.programme?.total_weeks ?? null;
  const totalScore =
    enrollment?.total_score !== null && enrollment?.total_score !== undefined
      ? Math.round(enrollment.total_score)
      : null;
  const programmeCompleted = enrollment?.programme?.status === "completed";

  const menuItems = [
    { icon: LayoutGrid, label: "Dashboard", path: "/" },
    { icon: ClipboardList, label: "Work Packages", path: "/timeline" },
  ];

  const guideItems = [
    { icon: Map, label: "How the programme works", path: "/how-the-programme-works" },
    { icon: Compass, label: "How to get started", path: "/how-to-get-started" },
    { icon: BookOpen, label: "How to get the best feedback", path: "/marking-guide" },
    { icon: HelpCircle, label: "FAQ and Help", path: "/support" },
  ];

  const preferenceItems = [{ icon: User, label: "Account", path: "/account" }];

  const handleNavClick = () => {
    close();
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={close}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <div
        className={cn(
          "fixed md:static inset-y-0 left-0 z-50 w-full md:w-64 bg-sidebar text-sidebar-foreground h-dvh pt-[env(safe-area-inset-top)] flex flex-col",
          "transform transition-transform duration-300 ease-in-out md:transform-none",
          isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        {/* Logo and Title */}
        <div className="flex-shrink-0 px-[18px] pt-[22px]">
          <div className="flex items-start justify-between gap-3 px-1.5 pb-5">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <img src="/dfr-logo.svg" alt="DFR Logo" className="h-9 w-auto flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <ProgrammeSwitcher />
              </div>
            </div>
            {/* X Button - Mobile only */}
            <button
              onClick={close}
              className="md:hidden w-10 h-10 rounded-xl border border-sidebar-border flex items-center justify-center hover:bg-sidebar-accent transition-colors flex-shrink-0"
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Overall Feedback Card */}
          <div className="rounded-xl border border-[#F2EFE3]/[.22] border-t-2 border-t-brand bg-white/[.04] px-[18px] py-4">
            <span className="text-[11px] font-bold uppercase tracking-[.16em] text-[#F2EFE3]/70">
              Overall Feedback
            </span>
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-sidebar-muted" />
              </div>
            ) : (
              <div className="mt-3 flex items-center gap-3.5">
                <div className="relative h-16 w-16 flex-none">
                  <svg width="64" height="64" viewBox="0 0 64 64" className="-rotate-90">
                    <circle
                      cx="32"
                      cy="32"
                      r="27"
                      fill="none"
                      stroke="rgba(242,239,227,.14)"
                      strokeWidth="5"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="27"
                      fill="none"
                      className="stroke-brand"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeDasharray={`${((totalScore ?? 0) / 100) * RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-xl font-extrabold text-white">
                    {totalScore ?? 0}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-[14.5px] font-bold text-white">
                    {totalScore === null
                      ? "No feedback yet"
                      : programmeCompleted
                        ? "Final grade"
                        : totalScore >= 65
                          ? "On track"
                          : "Below target"}
                  </div>
                  <div className="mt-[3px] font-serif text-[12.5px] italic text-[#F2EFE3]/60">
                    {programmeCompleted
                      ? "Programme complete"
                      : `Week ${currentWeek || "--"}${totalWeeks ? ` of ${totalWeeks}` : ""}`}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Menu */}
        <div className="flex-1 min-h-0 px-[18px] pb-6 overflow-y-auto">
          <NavSection title="Menu" items={menuItems} onNavigate={handleNavClick} />
          <NavSection title="Guides" items={guideItems} onNavigate={handleNavClick} />
          <NavSection title="Preference" items={preferenceItems} onNavigate={handleNavClick} />
        </div>
      </div>
    </>
  );
}
